import { useSyncExternalStore } from 'react';
import { FONT_REGISTRY } from '../components/fontList';

/**
 * Canvas text only uses a webfont once the browser has actually LOADED it. Our
 * caption/hook previews draw on a canvas, and a font that is declared via
 * @font-face but not used by any DOM element is never fetched - so the canvas
 * falls back (the "font didn't load" look), which hit the cute fonts (Baloo2,
 * Fredoka, Luckiest Guy, Chewy, Nunito) since nothing in the DOM uses them.
 *
 * This module explicitly loads every registered family (across the weights we
 * ship) and exposes a hook that flips to true once they're ready, so a canvas
 * component re-renders and redraws with the real fonts.
 */

let ready = false;
let started = false;
const listeners = new Set<() => void>();

function begin(): void {
  if (started) return;
  started = true;
  const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
  if (!fonts) {
    ready = true;
    return;
  }
  const jobs: Promise<unknown>[] = [];
  for (const f of FONT_REGISTRY) {
    for (const w of f.weights) {
      // A weight the family doesn't ship just resolves empty; ignore failures.
      try { jobs.push(fonts.load(`${w} 40px "${f.family}"`)); } catch { /* ignore */ }
    }
  }
  Promise.allSettled([...jobs, fonts.ready]).then(() => {
    ready = true;
    listeners.forEach((l) => l());
  });
}

/** true once all caption fonts are loaded; re-renders the caller when it flips. */
export function useCaptionFontsReady(): boolean {
  begin();
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => ready,
    () => true,
  );
}
